/**
 * Countdown Timer for Listicle Page
 * Handles countdown timers in announcement bar and CTA sections
 * Supports both "hours from now" and "end of day" modes
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'listicle_countdown_end';
  const STORAGE_KEY_DATE = 'listicle_countdown_date';

  /**
   * Get end of current day (midnight tonight) timestamp
   * @returns {number} - Unix timestamp of midnight tonight
   */
  function getEndOfDay() {
    const now = new Date();
    const endOfDay = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23, 59, 59, 999
    );
    return endOfDay.getTime();
  }

  /**
   * Get today's date string for comparison
   * @returns {string} - Date string in YYYY-MM-DD format
   */
  function getTodayString() {
    const now = new Date();
    return now.getFullYear() + '-' +
           String(now.getMonth() + 1).padStart(2, '0') + '-' +
           String(now.getDate()).padStart(2, '0');
  }

  /**
   * Get or initialize the countdown end time
   * @param {number} hours - Number of hours for countdown (ignored if useEndOfDay is true)
   * @param {boolean} useEndOfDay - If true, countdown ends at midnight
   * @returns {number} - Unix timestamp of end time
   */
  function getEndTime(hours, useEndOfDay) {
    const now = Date.now();

    if (useEndOfDay) {
      // End of day mode - always use midnight tonight
      // Check if we've stored today's date
      const storedDate = localStorage.getItem(STORAGE_KEY_DATE);
      const today = getTodayString();

      if (storedDate !== today) {
        // New day - reset the countdown
        localStorage.setItem(STORAGE_KEY_DATE, today);
        localStorage.removeItem(STORAGE_KEY);
      }

      return getEndOfDay();
    }

    // Hours-based mode (original behavior)
    const stored = localStorage.getItem(STORAGE_KEY);

    if (stored) {
      const endTime = parseInt(stored, 10);
      // If stored end time is still in the future, use it
      if (endTime > now) {
        return endTime;
      }
    }

    // Initialize new countdown
    const endTime = now + (hours * 60 * 60 * 1000);
    localStorage.setItem(STORAGE_KEY, endTime.toString());
    return endTime;
  }

  /**
   * Format time remaining into HH:MM:SS
   * @param {number} ms - Milliseconds remaining
   * @returns {object} - Object with hours, minutes, seconds
   */
  function formatTime(ms) {
    if (ms <= 0) {
      return { hours: '00', minutes: '00', seconds: '00' };
    }

    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return {
      hours: hours.toString().padStart(2, '0'),
      minutes: minutes.toString().padStart(2, '0'),
      seconds: seconds.toString().padStart(2, '0')
    };
  }

  /**
   * Update all countdown displays
   * @param {object} time - Formatted time object
   */
  function updateDisplays(time) {
    // Update announcement bar countdown
    const announcementCountdowns = document.querySelectorAll('.announcement-bar-listicle__countdown');
    announcementCountdowns.forEach(function(countdown) {
      const hoursEl = countdown.querySelector('[data-hours]');
      const minutesEl = countdown.querySelector('[data-minutes]');
      const secondsEl = countdown.querySelector('[data-seconds]');

      if (hoursEl) hoursEl.textContent = time.hours;
      if (minutesEl) minutesEl.textContent = time.minutes;
      if (secondsEl) secondsEl.textContent = time.seconds;
    });

    // Update CTA countdown
    const ctaCountdowns = document.querySelectorAll('[data-cta-countdown]');
    ctaCountdowns.forEach(function(countdown) {
      countdown.textContent = time.hours + ':' + time.minutes + ':' + time.seconds;
    });
  }

  /**
   * Initialize countdown timer
   */
  function initCountdown() {
    // Check for end-of-day mode from any countdown element
    const announcementCountdown = document.querySelector('.announcement-bar-listicle__countdown');
    const ctaCountdown = document.querySelector('[data-cta-countdown]');

    let useEndOfDay = false;
    let hours = 24; // Default

    // Check announcement bar settings
    if (announcementCountdown) {
      if (announcementCountdown.getAttribute('data-end-of-day') === 'true') {
        useEndOfDay = true;
      } else {
        const configuredHours = announcementCountdown.getAttribute('data-countdown-hours');
        if (configuredHours) {
          hours = parseInt(configuredHours, 10) || 24;
        }
      }
    }

    // Check CTA settings (end-of-day takes precedence if any element uses it)
    if (ctaCountdown && ctaCountdown.getAttribute('data-end-of-day') === 'true') {
      useEndOfDay = true;
    }

    const endTime = getEndTime(hours, useEndOfDay);

    function tick() {
      const now = Date.now();
      const remaining = endTime - now;
      const time = formatTime(remaining);
      updateDisplays(time);

      // Continue ticking if time remains
      if (remaining > 0) {
        requestAnimationFrame(function() {
          setTimeout(tick, 1000 - (Date.now() % 1000));
        });
      } else if (useEndOfDay) {
        // If end of day mode and timer expired, reinitialize for new day
        setTimeout(function() {
          initCountdown();
        }, 1000);
      }
    }

    // Start the countdown
    tick();
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCountdown);
  } else {
    initCountdown();
  }

  // Re-initialize on Shopify section load (for theme customizer)
  document.addEventListener('shopify:section:load', function(event) {
    if (event.target.querySelector('.announcement-bar-listicle__countdown') ||
        event.target.querySelector('[data-cta-countdown]')) {
      initCountdown();
    }
  });
})();
